import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { apiClient } from '@/api/apiClient';
import { UserSession } from '@/types/user';
import { AuthResult, OtpType, VerifyOtpResult } from '@/types/auth';
import { trackAccountCreated } from '@/lib/gtm';

type AuthContextType = {
  isAuthenticated: boolean;
  isLoading: boolean;
  userEmail?: string;
  userSession?: UserSession;
  refreshUserSession: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<{ success: boolean; message?: string }>;
  signUp: (email: string, password: string, fullName?: string) => Promise<AuthResult>;
  signOut: () => Promise<void>;
  verifyOtp: (email: string, token: string, type: OtpType) => Promise<VerifyOtpResult>;
  resendOtp: (email: string, type: OtpType) => Promise<{ success: boolean; message: string }>;
  requestPasswordReset: (email: string) => Promise<{ success: boolean; message: string }>;
  updatePassword: (newPassword: string) => Promise<{ success: boolean; message: string }>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [userEmail, setUserEmail] = useState<string | undefined>(undefined);
  const [userSession, setUserSession] = useState<UserSession | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const syncSessionToLocalStorage = (session: any | null) => {
    if (session?.access_token) {
      localStorage.setItem('access_token', session.access_token);
    } else {
      localStorage.removeItem('access_token');
    }
    if (session?.refresh_token) {
      localStorage.setItem('refresh_token', session.refresh_token);
    } else {
      localStorage.removeItem('refresh_token');
    }
  };

  const fetchUserSession = async () => {
    try {
      const response = await apiClient.get('/auth/me');
      setUserSession(response.data);
      return response.data as UserSession;
    } catch (error) {
      console.error('Failed to fetch user session:', error);
      setUserSession(undefined);
      return undefined;
    }
  };

  const refreshUserSession = async () => {
    await fetchUserSession();
  };

  useEffect(() => {
    let isMounted = true;
    supabase.auth.getSession().then(async ({ data }) => {
      if (!isMounted) return;
      const session = data.session;
      setIsAuthenticated(!!session);
      setUserEmail(session?.user?.email ?? undefined);
      syncSessionToLocalStorage(session);

      if (session) {
        await fetchUserSession();
      }

      setIsLoading(false);
    });

    const { data: authListener } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setIsAuthenticated(!!session);
      setUserEmail(session?.user?.email ?? undefined);
      syncSessionToLocalStorage(session);

      if (session) {
        await fetchUserSession();
      } else {
        setUserSession(undefined);
      }
    });

    return () => {
      isMounted = false;
      authListener.subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      return { success: false, message: error.message };
    }
    syncSessionToLocalStorage(data.session);
    setIsAuthenticated(!!data.session);
    setUserEmail(data.session?.user?.email ?? undefined);

    if (data.session) {
      await fetchUserSession();
    }

    return { success: true };
  };

  const signUp = async (email: string, password: string, fullName?: string): Promise<AuthResult> => {
    const name = fullName?.trim();
    const options = name
      ? { data: { full_name: name, name } }
      : undefined;
    const { data, error } = await supabase.auth.signUp({ email, password, options });
    if (error) {
      return { success: false, message: error.message, requiresVerification: false };
    }

    if (data.user && !data.session && data.user.identities && data.user.identities.length === 0) {
      return { success: false, message: 'User already registered', requiresVerification: false };
    }

    if (data.session) {
      syncSessionToLocalStorage(data.session);
      setIsAuthenticated(true);
      setUserEmail(data.session.user?.email ?? undefined);
      await fetchUserSession();
      trackAccountCreated(data.session.user?.email);
      return { success: true, message: '', requiresVerification: false };
    }

    return { success: true, message: 'Verification code sent to your email', requiresVerification: true };
  };

  const verifyOtp = async (email: string, token: string, type: OtpType): Promise<VerifyOtpResult> => {
    const { data, error } = await supabase.auth.verifyOtp({ email, token, type });

    if (error) {
      return { success: false, message: error.message };
    }

    if (data.session) {
      syncSessionToLocalStorage(data.session);
      setIsAuthenticated(true);
      setUserEmail(data.session.user?.email ?? undefined);
      await fetchUserSession();

      // Track account creation for signup verification
      if (type === 'signup') {
        trackAccountCreated(email);
      }
    }

    return { success: true, message: 'Verification successful' };
  };

  const resendOtp = async (email: string, type: OtpType): Promise<{ success: boolean; message: string }> => {
    if (type === 'signup') {
      const { error } = await supabase.auth.resend({ type: 'signup', email });
      if (error) return { success: false, message: error.message };
      return { success: true, message: 'New code sent' };
    }

    const { error } = await supabase.auth.resetPasswordForEmail(email);
    if (error) return { success: false, message: error.message };
    return { success: true, message: 'New code sent' };
  };

  const requestPasswordReset = async (email: string): Promise<{ success: boolean; message: string }> => {
    const { error } = await supabase.auth.resetPasswordForEmail(email);
    if (error) return { success: false, message: error.message };
    return { success: true, message: 'Reset code sent to your email' };
  };

  const updatePassword = async (newPassword: string): Promise<{ success: boolean; message: string }> => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) return { success: false, message: error.message };
    return { success: true, message: 'Password updated successfully' };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    syncSessionToLocalStorage(null);
    setIsAuthenticated(false);
    setUserEmail(undefined);
    setUserSession(undefined);
  };

  const value = useMemo<AuthContextType>(() => ({
    isAuthenticated,
    isLoading,
    userEmail,
    userSession,
    refreshUserSession,
    signIn,
    signUp,
    signOut,
    verifyOtp,
    resendOtp,
    requestPasswordReset,
    updatePassword,
  }), [isAuthenticated, isLoading, userEmail, userSession]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};
